import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import { PostStatus } from '../../data-layer/posts/constants';
import * as postsService from '../../data-layer/posts/service';
import { JsonLd } from '../../structuredData/JsonLd';
import { buildPostMediaJsonLd } from '../../structuredData/builders/buildPostMediaJsonLd';
import { BusinessStatisticSummary } from '../../ui/business/BusinessStatisticSummary/BusinessStatisticSummary';
import { MessengerButton } from '../../ui/business/MessengerWidget';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconAlarm } from '../../ui/icon/IconAlarm';
import { IconClock } from '../../ui/icon/IconClock';
import { IconDeclinedFilled } from '../../ui/icon/IconDeclinedFilled';
import { IconKebab } from '../../ui/icon/IconKebab';
import { IconProductWarning } from '../../ui/icon/IconProductWarning';
import { Button } from '../../ui/input/Button';
import { IconButton } from '../../ui/input/IconButton';
import { Box } from '../../ui/layout/Box';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { Flex } from '../../ui/layout/Flex';
import { PostCardSkeleton } from '../../ui/posts/PostCard/PostCard';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { PostDetailsCard } from '../../ui/posts/PostDetailsCard/PostDetailsCard';
import { PostStatisticSkeleton } from '../../ui/posts/PostStatistic';
import { DeclinedReason } from '../../ui/posts/types';
import { Typography } from '../../ui/typography/Typography';
import {
    hasAccessToPostStat,
    isBloggerBusiness,
    canSeeSupportChat,
} from '../../utils/accessService';
import { EventSource } from '../../utils/analyticsService';
import { buildImageSource } from '../../utils/buildImageSource';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';
import { trackEcommerceEvent } from '../../utils/ecommerce';
import { useIsMobile } from '../../utils/getIsMobile';
import { ymTrackChatClick } from '../../utils/metrika';
import { makeTransition } from '../../utils/styles/makeTransition';
import { useMeta } from '../../utils/useMeta';
import { useCurrentUser } from '../users/useCurrentUser';
import { useCanonicalUrl } from '../utils/useCanonicalUrl';
import { PostsGrid } from './PostsGrid/PostsGrid';
import { usePostModel } from './usePostModel';
import { withPostCallbacks } from './withPostCallbacks';
import type { PostModel, PostStatModel } from '../../data-layer/posts/types';
import type { UserModel } from '../../data-layer/user/types';

const MODERATION_RULES_LINK = 'https://yandex.ru/support2/rythm/ru/buisness/moderation';

const EnhancedPostCard = withPostCallbacks(PostDetailsCard, EventSource.POST);

const EnhancedPostMenu = withPostCallbacks(PostContextMenu, EventSource.POST);

const buildMeta = (model: PostModel | undefined) => {
    if (!model) {
        return {};
    }

    const {
        content: [
            {
                media: [firstMedia],
            },
        ],
    } = model;

    return {
        title: capitalizeFirstLetter(
            `${model.text.split(/\s+/).slice(0, 10).join(' ')}... - ${
                model.business ? model.business.displayName : ''
            } - Яндекс Ритм`,
        ),
        description: model.text,
        openGraph: {
            title: `Яндекс Ритм | ${model.business ? model.business.publicId : ''}`,
            description: model.text,
            image:
                'previewFormats' in firstMedia
                    ? buildImageSource(firstMedia.previewFormats)
                    : 'firstFrameUrl' in firstMedia
                      ? firstMedia.firstFrameUrl
                      : firstMedia.url,
        },
    };
};

const renderAlertElement = (onChatClick: () => void, post?: PostModel, currentUser?: UserModel) => {
    if (!post) {
        return null;
    }

    const editButton = (
        <Button
            as={Link}
            to={`/posts/${post.id}/edit`}
            variant="filled"
            width="100%"
            color="secondary"
        >
            Отредактировать пост
        </Button>
    );

    const messengerButton = canSeeSupportChat(post.business?.id, currentUser) && (
        <MessengerButton
            onClick={onChatClick}
            buttonText="Написать в поддержку"
            message={`Здравствуйте! У меня есть вопрос по статусу модерации поста ${post.shareUrl}.`}
            targetPosition="bottom-right"
        />
    );

    if (post.declinedReason === DeclinedReason.NEED_PRODUCT) {
        return (
            <Box width="100%" px={2} py={1.5}>
                <Box p={1} width="100%" background="var(--background-secondary)" borderRadius={2}>
                    <Flex direction="row" alignItems="center" spacing={1} px={1} mb={1}>
                        <IconProductWarning size={24} color="var(--color-danger)" />
                        <Typography variant="caption" p={1}>
                            К посту необходимо добавить товар. Отредактируйте и повторно отправьте
                            на публикацию.
                        </Typography>
                    </Flex>
                    {editButton}
                </Box>
            </Box>
        );
    }

    if (post.status === PostStatus.DECLINED) {
        return (
            <Box width="100%" px={2} py={1.5}>
                <Box p={1} width="100%" background="var(--background-secondary)" borderRadius={2}>
                    <Flex direction="row" alignItems="center" spacing={1} px={1} mb={1}>
                        <IconDeclinedFilled size={24} color="var(--color-danger)" />
                        <Typography variant="caption" p={1}>
                            Пост отклонен{' '}
                            <Typography
                                as="a"
                                target="_blank"
                                display="inline"
                                href={MODERATION_RULES_LINK}
                                rel="nofollow noreferrer"
                                variant="caption"
                                color="link"
                            >
                                по правилам сервиса
                            </Typography>
                            . Отредактируйте и повторно отправьте на публикацию.
                        </Typography>
                    </Flex>
                    <Flex direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={1}>
                        {editButton}
                        {messengerButton}
                    </Flex>
                </Box>
            </Box>
        );
    }

    if (post.status === PostStatus.MODERATION) {
        return (
            <Box width="100%" px={2} py={1.5}>
                <Box width="100%" background="var(--background-secondary)" borderRadius={2} p={1}>
                    <Flex direction="row" alignItems="center" spacing={1} px={1}>
                        <IconClock size={24} color="var(--color-primary)" />
                        <Typography variant="caption">
                            Пост находится на модерации. После одобрения, он появится
                            у пользователей
                        </Typography>
                    </Flex>
                </Box>
            </Box>
        );
    }

    if (post.status === PostStatus.LIMITED) {
        return (
            <Box width="100%" px={2} py={1.5}>
                <Box width="100%" background="var(--background-secondary)" borderRadius={2} p={1}>
                    <Flex direction="row" alignItems="center" spacing={1} px={1} mb={1}>
                        <IconAlarm size={24} color="var(--color-danger)" />
                        <Typography variant="caption" p={1}>
                            Данный пост доступен пользователям только в вашем аккаунте и не
                            участвует в рекомендательных процессах. Это может быть связано с
                            содержанием в посте возрастных тематик. Подробнее про правила сервиса
                            можно прочитать{' '}
                            <Typography
                                as="a"
                                target="_blank"
                                display="inline"
                                href={MODERATION_RULES_LINK}
                                rel="nofollow noreferrer"
                                variant="caption"
                                color="link"
                            >
                                по правилам сервиса
                            </Typography>
                            .
                        </Typography>
                    </Flex>
                    <Flex direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={1}>
                        {editButton}
                        {messengerButton}
                    </Flex>
                </Box>
            </Box>
        );
    }

    return null;
};

interface PostStatisticProps {
    stats: PostStatModel;
}

export interface PostDetailsLocationState {
    fromCollectionId?: string;
    fromBusinessId?: string;
    dialogName?: string;
}

export function PostDetails() {
    const params = useParams();
    const locationState = useLocation().state as PostDetailsLocationState | null;
    const postId = params.postId!;
    const post = usePostModel({ postId });
    const currentUser = useCurrentUser();
    const canonicalUrl = useCanonicalUrl();
    const [postStatistic, setPostStatistic] = useState<PostStatisticProps | undefined>(undefined);
    const isMobile = useIsMobile();
    const isBlogger = isBloggerBusiness(post?.business?.type);

    useMeta(buildMeta(post));

    if (post && post.status === PostStatus.DELETED) {
        return <Navigate to="/" replace />;
    }

    const { loading } = useAsync(async () => {
        setPostStatistic(undefined);
        if (hasAccessToPostStat(post?.business?.id, currentUser, post)) {
            setPostStatistic(await postsService.loadPostStatistic(postId));
        }
    }, [postId, currentUser]);

    const postGridEnabled = !hasAccessToPostStat(post?.business?.id, currentUser, post);

    useEffect(() => {
        if (post) {
            trackEcommerceEvent({
                post,
                trackEcommerceView: true,
            });
        }
    }, [post]);

    const onChatClick = useCallback(() => {
        ymTrackChatClick({
            page: 'Пост',
            userLogin: currentUser?.login,
            businessId: post?.business?.publicId,
            postId: post?.id,
        });
    }, [post, currentUser?.login]);

    const alertElement = useMemo(
        () => renderAlertElement(onChatClick, post, currentUser),
        [post, currentUser, onChatClick],
    );

    const enableStat = hasAccessToPostStat(post?.business?.id, currentUser, post);
    const statistic = postStatistic?.stats;
    const isService = post?.business?.isService;
    const postStat = useMemo(() => {
        if (!enableStat) {
            return null;
        }

        if (loading) {
            return <PostStatisticSkeleton />;
        }

        if (!statistic) {
            return null;
        }

        return (
            <Flex width="100%" background="var(--background-primary)" borderRadius={2} mt={2}>
                <BusinessStatisticSummary
                    statistic={statistic}
                    hideSubscribers
                    hideDetails={isBlogger}
                    isService={isService}
                />
            </Flex>
        );
    }, [enableStat, loading, statistic, isBlogger, isService]);

    return (
        <DashboardPage
            adaptiveWidth={!postGridEnabled}
            minWidth={{ sm: 700 }}
            transparentHeader={!alertElement}
            gridFeed={postGridEnabled}
            menuSlot={({ appMenuItem }) =>
                post ? (
                    <EnhancedPostMenu
                        fromCollectionId={locationState?.fromCollectionId}
                        fromBusinessId={locationState?.fromBusinessId}
                        index={0}
                        post={post}
                        user={currentUser}
                        appMenuItem={appMenuItem}
                        trigger={
                            <Box transition={makeTransition({ styles: ['opacity'], ms: 200 })}>
                                <IconButton size="m">
                                    <IconKebab
                                        color="var(--color-primary)"
                                        dataTestId={DataTestId.POST_CARD_KEBAB}
                                    />
                                </IconButton>
                            </Box>
                        }
                    />
                ) : null
            }
        >
            <JsonLd data={buildPostMediaJsonLd(post, canonicalUrl)} />
            <Flex
                direction="column"
                background="var(--background-primary)"
                overflow="hidden"
                margin="auto"
                mt={{ sm: 2 }}
                width={isMobile === false ? 'min-content' : undefined}
                maxWidth={{ sm: '100%' }}
                borderRadius={{ sm: 2 }}
            >
                {post ? (
                    <Box maxWidth={{ sm: '100%' }}>
                        {alertElement}
                        <EnhancedPostCard
                            key={post.id}
                            index={0}
                            post={post}
                            borderRadius={0}
                            fromCollectionId={locationState?.fromCollectionId}
                            fromBusinessId={locationState?.fromBusinessId}
                            user={currentUser}
                            renderMenu={(trigger) =>
                                isMobile ? null : (
                                    <EnhancedPostMenu
                                        fromCollectionId={locationState?.fromCollectionId}
                                        fromBusinessId={locationState?.fromBusinessId}
                                        index={0}
                                        trigger={trigger}
                                        post={post}
                                        user={currentUser}
                                    />
                                )
                            }
                        />
                    </Box>
                ) : (
                    <PostCardSkeleton />
                )}
            </Flex>
            <Box width="100%">
                {postStat}
                {postGridEnabled ? <PostsGrid excludePostsIds={[postId]} titleEnabled /> : null}
            </Box>
        </DashboardPage>
    );
}
